import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "../../axios";
import { toast } from "react-toastify";
import ShareIcon from "@mui/icons-material/Share";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import ReplyIcon from "@mui/icons-material/Reply";
import EditIcon from "@mui/icons-material/Edit";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CancelIcon from "@mui/icons-material/Cancel";
import DescriptionIcon from "@mui/icons-material/Description";
import RefreshIcon from "@mui/icons-material/Refresh";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import html2pdf from "html2pdf.js";

import {
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
} from "@mui/material";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import dayjs from "dayjs";

import CompanyHeader from '../../components/CompanyHeader';
import {
  ReportPage, ReportFilters, ReportSelect, ReportTableCard,
  useReportDateRange, reportGridSx,
} from '../../components/Reports/ReportKit';
import { getAllowedBranchIds } from '../../utils/tokenUtils';

export default function AllInvoicesReport() {
  const decimalPlaces = 2;

  // رقم الشركة
  const storedCompanyId = localStorage.getItem("companyId");
  const companyId = storedCompanyId ? parseInt(storedCompanyId, 10) : null;
  const [companyInfo, setCompanyInfo] = useState(null);
  const defaultBranchId = parseInt(localStorage.getItem("branchId"), 10);

  // invoiceTypeId من URL
  const [searchParams] = useSearchParams();
  const invoiceTypeIdParam = searchParams.get("invoiceTypeId") || "";
  const invoiceTypeId = invoiceTypeIdParam ? Number(invoiceTypeIdParam) : null;

  const statusParam = searchParams.get("invoiceStatus");
  const [statusFilter, setStatusFilter] = useState(statusParam ?? "");
  const [InvtypeidFilter, setinvtypidFilter] = useState(invoiceTypeIdParam ?? "");
  const [branchFilter, setBranchFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [branches, setBranches] = useState([]);

  // التواريخ — اليوم افتراضيًا (موحّد لكل التقارير)
  const range = useReportDateRange("today");
  const { dateFrom, dateTo } = range;

  // البيانات
  const [allInvoices, setAllInvoices] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!companyId) return;
    
    // Fetch Company Info
    axios.get(`/Company/${companyId}`)
      .then(res => setCompanyInfo(res.data))
      .catch(err => console.error("Failed to load company info", err));

    axios
      .get("/InvoiceTypes", { params: { companyId } })
      .then((res) => setInvoiceTypes(res.data || []))
      .catch(() => {});

    // جلب الفروع وتصفيتها حسب الفروع المسموحة
    axios.get(`/Branch/by-company/${companyId}`).then(r => {
      const allowedBranchIds = getAllowedBranchIds();
      // ✅ القاعدة: إذا كانت القائمة فارغة → المستخدم له صلاحية على كل الفروع
      if (allowedBranchIds && allowedBranchIds.length > 0) {
        const filteredBranches = r.data.filter(b => allowedBranchIds.includes(b.id));
        setBranches(filteredBranches);
        if (defaultBranchId > 0 && filteredBranches.some(b => b.id === defaultBranchId)) {
          setBranchFilter(defaultBranchId.toString());
        } else if (filteredBranches.length > 0) {
          setBranchFilter(filteredBranches[0].id.toString());
        }
      } else {
        // المستخدم له صلاحية على كل الفروع → عرض الكل بدون فلترة
        setBranches(r.data);
      }
    });
  }, [companyId, defaultBranchId]);

  // جلب الفواتير
  const fetchAllInvoices = useCallback(async () => {
    if (!companyId) {
      console.error("⚠️ CompanyId not found in localStorage");
      return;
    }
    setLoading(true);
    try {
      const typeIdToSend = InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId;
      const params = {
        ...(typeIdToSend !== null && typeIdToSend !== undefined ? { invoiceTypeId: typeIdToSend } : {}),
        dateFrom: dateFrom + "T00:00:00",
        dateTo: dateTo + "T23:59:59",
        ...(statusFilter !== "" ? { invoiceStatus: statusFilter } : {}),
        ...(branchFilter !== "" ? { branchId: Number(branchFilter) } : {}),
      };
      const res = await axios.get(`/Invoice/Invoice-report/${companyId}`, {
        params,
      });
      setAllInvoices(res.data);
    } catch (error) {
      console.error("❌ خطأ في جلب تقرير الفواتير:", error);
      toast.error("فشل في تحميل تقرير الفواتير");
    } finally {
      setLoading(false);
    }
  }, [companyId, dateFrom, dateTo, invoiceTypeId, statusFilter, InvtypeidFilter, branchFilter]);

  useEffect(() => {
    fetchAllInvoices();
  }, [fetchAllInvoices]);

  const getInvoiceHtmlElement = (data, isPos) => {
    const element = document.createElement("div");
    element.setAttribute("dir", "rtl");
    element.style.fontFamily = "'Cairo', 'Tajawal', sans-serif";
    
    // إعدادات الخطوط
    const fontStyle = `
      @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;600;700&family=Tajawal:wght@400;500;700&display=swap');
      body { font-family: 'Cairo', sans-serif; }
    `;
    const styleElement = document.createElement("style");
    styleElement.innerHTML = fontStyle;
    element.appendChild(styleElement);

    if (isPos) {
      // 🟢 تصميم فاتورة نقاط البيع (POS) - مطابق تماماً لـ InvoicePrintPos.jsx
      element.style.width = "80mm";
      element.style.padding = "5mm";
      element.style.background = "#fff";
      element.style.color = "#000";
      element.style.fontSize = "12px";
      element.style.margin = "0 auto"; // توسيط

      element.innerHTML += `
        <div style="text-align:center; margin-bottom:10px;">
          ${data.company?.logoUrl ? `<img src="${data.company.logoUrl}" style="max-width:60px; max-height:60px; margin:0 auto; display:block;" />` : ''}
          <h2 style="margin:5px 0; font-size:16px; font-weight:800;">${data.company?.name || "اسم الشركة"}</h2>
          ${data.company?.nameEn ? `<h3 style="margin:2px 0; font-size:12px; font-weight:bold;">${data.company.nameEn}</h3>` : ''}
          <p style="margin:0; font-size:11px;">${data.company?.address || ""}</p>
          <p style="margin:0; font-size:11px;" dir="ltr">${data.company?.phone || ""}</p>
          ${data.company?.taxNumber ? `<p style="margin:2px 0; font-size:11px; font-weight:bold;">الرقم الضريبي: ${data.company.taxNumber}</p>` : ''}
          <div style="border-top:1px dashed #000; margin:8px 0;"></div>
        </div>

        <div style="display:flex; flex-direction:column; gap:4px; font-size:11px; margin-bottom:10px;">
          <div style="display:flex; justify-content:space-between;">
            <span>رقم الفاتورة:</span>
            <span style="font-weight:bold;">${data.invoiceNumber}</span>
          </div>
          <div style="display:flex; justify-content:space-between;">
            <span>التاريخ:</span>
            <span>${new Date(data.issueDate).toLocaleDateString("en-CA")}</span>
          </div>
          <div style="display:flex; justify-content:space-between;">
            <span>الوقت:</span>
            <span>${new Date(data.issueDate).toLocaleTimeString("en-US", { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
          ${data.customer?.name ? `
          <div style="display:flex; flex-direction:column; align-items:flex-start;">
            <span>العميل:</span>
            <span style="font-weight:bold;">${data.customer.name}</span>
          </div>` : ''}
          <div style="display:flex; justify-content:space-between;">
             <span>طريقة الدفع:</span>
             <span>${data.paymentMethodName || '-'}</span>
          </div>
        </div>
        <div style="border-top:1px dashed #000; margin:8px 0;"></div>

        <div style="margin-bottom:10px;">
          <div style="display:flex; justify-content:space-between; font-weight:bold; font-size:12px; margin-bottom:4px;">
             <span>الصنف</span>
             <span>الإجمالي</span>
          </div>
          <div style="border-top:1px dotted #ccc; margin:5px 0;"></div>
          ${data.items.map(item => {
            const qty = item.quantity ?? 0;
            const price = item.unitPrice ?? 0;
            const discount = item.discount ?? 0;
            const taxValue = item.taxValue ?? 0;
            const itemTotal = qty * price;
            const itemNet = (itemTotal - discount) + taxValue;
            return `
              <div style="margin-bottom:6px; font-size:12px;">
                <div style="display:flex; justify-content:space-between; align-items:baseline;">
                  <span style="font-weight:bold; flex:1; margin-left:8px;">${item.productName || item.arabicName}</span>
                  <span style="font-weight:bold;">${itemNet.toFixed(2)}</span>
                </div>
                <div style="font-size:11px; color:#444; margin-top:1px;">
                  <span>${Number(qty).toFixed(2)} ${item.unitName || ''} × ${Number(price).toFixed(2)}</span>
                  ${discount > 0 ? `<span style="margin-right:5px;">(خصم: ${Number(discount).toFixed(2)})</span>` : ''}
                  ${taxValue > 0 ? `<span style="margin-right:5px;">(ضريبة: ${Number(taxValue).toFixed(2)})</span>` : ''}
                </div>
              </div>
            `;
          }).join('')}
          <div style="border-top:1px dashed #000; margin:8px 0;"></div>
        </div>

        <div style="margin-bottom:15px;">
          <div style="display:flex; justify-content:space-between; font-size:12px; margin-bottom:3px;">
            <span>الإجمالي (قبل الضريبة):</span>
            <span>${data.totals.subtotal.toFixed(2)}</span>
          </div>
          ${data.totals.discountTotal > 0 ? `
          <div style="display:flex; justify-content:space-between; font-size:12px; margin-bottom:3px;">
            <span>مجموع الخصم:</span>
            <span>-${data.totals.discountTotal.toFixed(2)}</span>
          </div>` : ''}
          <div style="display:flex; justify-content:space-between; font-size:12px; margin-bottom:3px;">
            <span>ضريبة القيمة المضافة:</span>
            <span>${data.totals.totalVAT.toFixed(2)}</span>
          </div>
          <div style="border-top:2px solid #000; margin:8px 0;"></div>
          <div style="display:flex; justify-content:space-between; align-items:center; margin:5px 0;">
            <span style="font-size:14px; font-weight:bold;">الصافي النهائي:</span>
            <span style="font-size:18px; font-weight:bold;">${data.totals.net.toFixed(2)}</span>
          </div>
          <div style="border-top:1px dotted #ccc; margin:5px 0;"></div>
          <div style="display:flex; justify-content:space-between; font-size:12px; font-weight:bold; margin-bottom:3px;">
            <span>المبلغ المدفوع:</span>
            <span>${(data.paidAmount || 0).toFixed(2)}</span>
          </div>
          ${(data.paidAmount - data.totals.net) > 0 ? `
          <div style="display:flex; justify-content:space-between; font-size:12px; font-weight:bold;">
            <span>الباقي للعميل:</span>
            <span>${(data.paidAmount - data.totals.net).toFixed(2)}</span>
          </div>` : ''}
        </div>

        ${data.qrCodeUrl ? `
        <div style="text-align:center; margin:10px 0;">
          <img src="${data.qrCodeUrl}" style="width:100px; height:100px; margin:0 auto; display:block;" />
        </div>` : ''}

        <div style="text-align:center; font-size:11px; margin-top:10px;">
          <p style="font-weight:bold; margin:0;">شكراً لزيارتكم</p>
          ${data.company?.website ? `<p style="margin:0;">${data.company.website}</p>` : ''}
        </div>
      `;

    } else {
      // 🔵 تصميم الفاتورة القياسي (A4) - مطابق لـ InvoicePrint.jsx
      element.style.width = "210mm";
      element.style.minHeight = "297mm";
      element.style.padding = "10mm";
      element.style.margin = "auto";
      element.style.background = "#fff";
      element.style.color = "#000";

      element.innerHTML += `
        ${data.company ? `
        <div style="display:flex; justify-content:space-between; align-items:center; border-bottom:2px solid #eee; padding-bottom:10px; margin-bottom:20px;">
          <div style="text-align:right;">
             <h2 style="margin:0; color:#333;">${data.company.name}</h2>
             <p style="margin:5px 0; font-size:12px;">${data.company.address || ''}</p>
             <p style="margin:0; font-size:12px;">${data.company.phone || ''}</p>
             ${data.company.taxNumber ? `<p style="margin:5px 0; font-weight:bold; font-size:12px;">الرقم الضريبي: ${data.company.taxNumber}</p>` : ''}
          </div>
          ${data.company.logoUrl ? `<img src="${data.company.logoUrl}" style="max-height:80px;" />` : ''}
          <div style="text-align:left;">
             <h2 style="margin:0; color:#333;">${data.company.nameEn || ''}</h2>
             <p style="margin:5px 0; font-size:12px;">${data.company.email || ''}</p>
             <p style="margin:0; font-size:12px;">${data.company.website || ''}</p>
          </div>
        </div>` : ''}

        <h2 style="text-align:center; border-top:2px solid #000; border-bottom:2px solid #000; padding:8px; margin-bottom:20px">
          ${data.invoiceTitle || "فاتورة ضريبية - Tax Invoice"}
        </h2>

        <table style="width:100%; margin-bottom:20px; font-size:14px;">
          <tbody>
            <tr>
              <td style="text-align:right;width:30%">رقم الفاتورة:</td>
              <td style="text-align:center;width:40%">${data.invoiceNumber}</td>
              <td style="text-align:left;width:30%">Invoice No.</td>
            </tr>
            <tr>
              <td style="text-align:right">تاريخ الفاتورة:</td>
              <td style="text-align:center">${new Date(data.issueDate).toLocaleDateString()}</td>
              <td style="text-align:left">Invoice Date</td>
            </tr>
            <tr>
              <td style="text-align:right">اسم العميل:</td>
              <td style="text-align:center">${data.customer?.name || "—"}</td>
              <td style="text-align:left">Customer Name</td>
            </tr>
            <tr>
              <td style="text-align:right">الرقم الضريبي:</td>
              <td style="text-align:center">${data.customer?.taxNumber || "—"}</td>
              <td style="text-align:left">VAT No.</td>
            </tr>
            <tr>
              <td style="text-align:right">الجوال:</td>
              <td style="text-align:center">${data.customer?.mobile || "—"}</td>
              <td style="text-align:left">Mobile</td>
            </tr>
            <tr>
              <td style="text-align:right">العنوان:</td>
              <td style="text-align:center">${data.customer?.address || "—"}</td>
              <td style="text-align:left">Address</td>
            </tr>
          </tbody>
        </table>

        <table border="1" style="width:100%; border-collapse:collapse; font-size:13px; margin-bottom:25px;">
          <thead style="background:#f1f1f1">
            <tr>
              <th style="padding:8px">الصنف</th>
              <th style="padding:8px">الكمية</th>
              <th style="padding:8px">الوحدة</th>
              <th style="padding:8px">السعر</th>
              <th style="padding:8px">الإجمالي</th>
              <th style="padding:8px">الخصم</th>
              <th style="padding:8px">الضريبة</th>
              <th style="padding:8px">الصافي</th>
            </tr>
          </thead>
          <tbody>
            ${data.items.map((item) => {
              const qty = item.quantity ?? 0;
              const price = item.unitPrice ?? 0;
              const discount = item.discount ?? 0;
              const tax = item.taxValue ?? 0;
              const total = qty * price;
              const net = (total - discount) + tax;
              return `
                <tr style="text-align:center">
                  <td style="padding:6px; font-weight:bold;">${item.arabicName || item.productName || "—"}</td>
                  <td style="padding:6px">${qty}</td>
                  <td style="padding:6px">${item.unitName || "—"}</td>
                  <td style="padding:6px">${price.toFixed(2)}</td>
                  <td style="padding:6px">${total.toFixed(2)}</td>
                  <td style="padding:6px">${discount.toFixed(2)}</td>
                  <td style="padding:6px">${tax.toFixed(2)}</td>
                  <td style="padding:6px;font-weight:bold">${net.toFixed(2)}</td>
                </tr>`;
            }).join("")}
          </tbody>
        </table>

        <div style="display:flex; justify-content:space-between; margin-top:20px;">
          <table style="width:65%; font-size:14px; border-collapse:collapse">
            <tbody>
              <tr><td style="text-align:right; padding:5px;">الإجمالي</td><td style="text-align:center; padding:5px;">${data.totals.subtotal.toFixed(2)} ﷼</td><td style="text-align:left; padding:5px;">Subtotal</td></tr>
              <tr><td style="text-align:right; padding:5px;">الخصم</td><td style="text-align:center; padding:5px;">${data.totals.discountTotal.toFixed(2)} ﷼</td><td style="text-align:left; padding:5px;">Discount</td></tr>
              <tr><td style="text-align:right; padding:5px;">الضريبة</td><td style="text-align:center; padding:5px;">${data.totals.totalVAT.toFixed(2)} ﷼</td><td style="text-align:left; padding:5px;">VAT</td></tr>
              <tr style="font-weight:bold; background:#f9f9f9;"><td style="text-align:right; padding:5px;">الصافي النهائي</td><td style="text-align:center; padding:5px;">${data.totals.net.toFixed(2)} ﷼</td><td style="text-align:left; padding:5px;">Net Total</td></tr>
            </tbody>
          </table>

          ${data.qrCodeUrl ? `
          <div style="text-align:center; width:30%;">
            <img src="${data.qrCodeUrl}" style="width:120px; height:120px; border:1px solid #ddd; padding:5px; border-radius:5px;" />
            <div style="margin-top:5px; font-size:12px;">رمز الاستجابة السريع</div>
          </div>` : ''}
        </div>

        <div style="position:absolute; bottom:10mm; left:0; width:100%; text-align:center; font-size:11px; color:#777; border-top:1px solid #ddd; padding-top:4px;">
           ${data.company?.address || ''} | ${data.company?.phone || ''} | ${data.company?.email || ''}
        </div>
      `;
    }

    return element;
  };

  const handleDirectPDF = async (invGlobalID, invoiceTypeId) => {
    try {
      const isPos = invoiceTypeId === 11; // 11 هو نوع فاتورة POS
      const res = await axios.get(`/Invoice/print/${invGlobalID}`);
      const data = res.data;
      const element = getInvoiceHtmlElement(data, isPos);

      const opt = isPos ? {
         margin: 0,
         filename: `فاتورة_${data.invoiceNumber}.pdf`,
         image: { type: "jpeg", quality: 0.98 },
         html2canvas: { scale: 2, useCORS: true },
         jsPDF: { unit: "mm", format: [80, 297], orientation: "portrait" },
         pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
       } : {
         margin: [8, 8, 8, 8],
         filename: `فاتورة_${data.invoiceNumber}.pdf`,
         image: { type: "jpeg", quality: 0.98 },
         html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
         jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
         pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
       };
 
       html2pdf().set(opt).from(element).save();
    } catch (err) {
      toast.error("❌ فشل في تحميل الفاتورة PDF");
      console.error(err);
    }
  };

  const handleSharePDF = async (invGlobalID, invoiceTypeId) => {
    try {
      const isPos = invoiceTypeId === 11;
      const toastId = toast.loading("جاري تجهيز الفاتورة للمشاركة...");
      const res = await axios.get(`/Invoice/print/${invGlobalID}`);
      const data = res.data;
      const element = getInvoiceHtmlElement(data, isPos);

      const opt = isPos ? {
         margin: 0,
         filename: `فاتورة_${data.invoiceNumber}.pdf`,
         image: { type: "jpeg", quality: 0.98 },
         html2canvas: { scale: 2, useCORS: true },
         jsPDF: { unit: "mm", format: [80, 297], orientation: "portrait" },
         pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
       } : {
         margin: [10, 10, 10, 10],
         filename: `فاتورة_${data.invoiceNumber}.pdf`,
         image: { type: "jpeg", quality: 0.98 },
         html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
         jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
         pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
       };
 
       const pdfBlob = await html2pdf().set(opt).from(element).output('blob');
      const file = new File([pdfBlob], `Invoice_${data.invoiceNumber}.pdf`, { type: 'application/pdf' });

      toast.dismiss(toastId);

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `فاتورة رقم ${data.invoiceNumber}`,
          text: `مرفق نسخة PDF من الفاتورة رقم ${data.invoiceNumber}`,
        });
      } else {
        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Invoice_${data.invoiceNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.info("تم تحميل الملف لأن المشاركة المباشرة غير مدعومة.");
      }
    } catch (err) {
      toast.dismiss();
      toast.error("❌ فشل في مشاركة الفاتورة");
      console.error(err);
    }
  };

  // تصفية البيانات بناءً على البحث
  const filteredInvoices = useMemo(() => {
    if (!searchTerm) return allInvoices;
    return allInvoices.filter(
      (inv) =>
        inv.invoiceNumber?.toString().includes(searchTerm) ||
        inv.partyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.invoiceTypeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        inv.paymentMethodName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allInvoices, searchTerm]);

  // الصفوف
  const mappedRows = useMemo(() => {
    return filteredInvoices.map((inv, index) => ({
      serial: index + 1,
      invGlobalID: inv.invGlobalID,
      invoiceTypeId: inv.invoiceTypeId,
      issueDate: dayjs(inv.issueDate).format("YYYY-MM-DD HH:mm"),
      invoiceNumber: inv.invoiceNumber?.toString().replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)),
      invoiceTypeName: inv.invoiceTypeName,
      partyName: inv.partyName,
      totalBeforeTax: inv.totalBeforeTax,
      discountTotal: inv.discountTotal,
      totalAfterDiscount: inv.totalAfterDiscount,
      taxTotal: inv.taxTotal,
      totalAfterTax: inv.totalAfterTax,
      paymentMethodName: inv.paymentMethodName,
      invoiceStatus: inv.invoicepaymentstatus,
    }));
  }, [filteredInvoices]);

  // المجاميع للبيانات المفلترة
  const totals = useMemo(() => {
    return filteredInvoices.reduce(
      (acc, inv) => ({
        totalBeforeTax: acc.totalBeforeTax + (Number(inv.totalBeforeTax) || 0),
        discountTotal: acc.discountTotal + (Number(inv.discountTotal) || 0),
        totalAfterDiscount: acc.totalAfterDiscount + (Number(inv.totalAfterDiscount) || 0),
        taxTotal: acc.taxTotal + (Number(inv.taxTotal) || 0),
        totalAfterTax: acc.totalAfterTax + (Number(inv.totalAfterTax) || 0),
      }),
      {
        totalBeforeTax: 0,
        discountTotal: 0,
        totalAfterDiscount: 0,
        taxTotal: 0,
        totalAfterTax: 0,
      }
    );
  }, [filteredInvoices]);

  // تنسيق العملة
  const formatCurrency = useCallback((value) => {
    if (value == null) return "0.00";
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'SAR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value).replace('SAR', '').trim();
  }, []);

  // تنسيق الأرقام
  const formatNumber = useCallback((value) => {
    if (value == null) return "0";
    return new Intl.NumberFormat('en-US').format(value);
  }, []);

  // الأعمدة
  const columns = useMemo(() => [
    { 
      field: "serial", 
      headerName: "م", 
      width: 60,
      align: 'center',
      headerAlign: 'center',
    },
    { 
      field: "issueDate", 
      headerName: "التاريخ", 
      width: 180,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography
          sx={{
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 500,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { 
      field: "invoiceNumber", 
      headerName: "رقم الفاتورة", 
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 'bold',
            color: 'primary.main'
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    { 
      field: "invoiceTypeName", 
      headerName: "نوع الفاتورة", 
      flex: 1, 
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
    },
    { 
      field: "partyName", 
      headerName: "العميل/المورد", 
      flex: 1, 
      minWidth: 200,
      align: 'center',
      headerAlign: 'center',
    },
    { 
      field: "totalBeforeTax", 
      headerName: "الإجمالي", 
      width: 130, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "discountTotal", 
      headerName: "الخصم", 
      width: 130, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            color: 'error.main',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "totalAfterDiscount", 
      headerName: "بعد الخصم", 
      width: 140, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "taxTotal", 
      headerName: "الضريبة", 
      width: 130, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            color: 'warning.main',
            fontWeight: 'bold'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "totalAfterTax", 
      headerName: "الصافي", 
      width: 140, 
      type: "number",
      align: 'center',
      headerAlign: 'center',
      valueFormatter: (params) => formatCurrency(params.value),
      renderCell: (params) => (
        <Typography 
          sx={{ 
            fontFamily: 'Arial, sans-serif',
            direction: 'ltr',
            unicodeBidi: 'embed',
            color: 'success.main',
            fontWeight: 'bold',
            fontSize: '1.1em'
          }}
        >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    { 
      field: "paymentMethodName", 
      headerName: "طريقة الدفع", 
      flex: 1, 
      minWidth: 150,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: "invoiceStatus",
      headerName: "الحالة",
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        let label = "";
        let color = "default";
        let icon = <DescriptionIcon />;

        switch (params.value) {
          case 0:
            label = "مسودة";
            color = "warning";
            icon = <EditIcon />;
            break;
          case 1:
            label = "معتمدة";
            color = "success";
            icon = <DoneAllIcon />;
            break;
          case 2:
            label = "مدفوعة";
            color = "info";
            icon = <AssignmentTurnedInIcon />;
            break;
          case 3:
            label = "ملغاة";
            color = "error";
            icon = <CancelIcon />;
            break;
          default:
            label = "غير معروف";
            color = "default";
            icon = <DescriptionIcon />;
        }

        return (
          <Chip
            icon={icon}
            label={label}
            color={color}
            variant="filled"
            size="small"
            sx={{ 
              fontWeight: "bold", 
              minWidth: "100px", 
              justifyContent: "start",
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s'
              }
            }}
          />
        );
      },
    },
    {
      field: "actions",
      headerName: "إجراءات",
      flex: 1,
      minWidth: 280,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const invGlobalID = params.row.invGlobalID;
        const status = params.row.invoiceStatus;

        return (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: 'center' }}>
            <Tooltip title="تعديل">
              <IconButton
                color="primary"
                size="small"
                onClick={() => window.open(`/invoices/edit/${invGlobalID}?invoiceTypeId=${params.row.invoiceTypeId ?? ""}`, "_blank")}
                sx={{
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="طباعة">
              <IconButton
                color="success"
                size="small"
                onClick={() => {
                    const invoiceType = params.row.invoiceTypeId || (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId);
                    if (invoiceType === 11) {
                         window.open(`/invoices/printPos/${invGlobalID}`, "_blank");
                    } else {
                        window.open(`/invoices/print/${invGlobalID}`, "_blank");
                    }
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'success.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="مشاركة PDF">
              <IconButton
                color="secondary"
                size="small"
                onClick={() => handleSharePDF(invGlobalID, params.row.invoiceTypeId)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'secondary.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <ShareIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="تحميل PDF مباشر">
              <IconButton
                color="info"
                size="small"
                onClick={() => handleDirectPDF(invGlobalID, params.row.invoiceTypeId)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'info.light',
                    transform: 'scale(1.1)',
                    transition: 'all 0.2s'
                  }
                }}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {status === 0 && (
              <Tooltip title="اعتماد">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={async () => {
                    try {
                      await axios.post(`/Invoice/approve/${invGlobalID}`);
                      toast.success("invoicesReport.invoiceApproved");
                      fetchAllInvoices();
                    } catch {
                      toast.error("❌ فشل في اعتماد الفاتورة");
                    }
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.light',
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <DoneAllIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {status === 1 && ((InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId) == 1 || (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId) == 2 || (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId) == 11) && (
              <Tooltip title="مرتجع">
                <IconButton
                  color="warning"
                  size="small"
                  onClick={() => {
                    const selectedType = (InvtypeidFilter !== "" ? Number(InvtypeidFilter) : invoiceTypeId);
                    const url = selectedType === 11
                      ? `/pos?typeId=3&returnOf=${invGlobalID}`
                      : `/invoices/new/3?returnOf=${invGlobalID}`;
                    window.open(url, "_blank");
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'warning.light',
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <ReplyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {invoiceTypeId === 10 && (
              <Tooltip title="توليد فاتورة">
                <IconButton
                  color="secondary"
                  size="small"
                  onClick={() =>
                    window.open(`/invoices/new/1?fromQuote=${invGlobalID}`, "_blank")
                  }
                  sx={{
                    '&:hover': {
                      backgroundColor: 'secondary.light',
                      transform: 'scale(1.1)',
                      transition: 'all 0.2s'
                    }
                  }}
                >
                  <DescriptionIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ], [formatCurrency, fetchAllInvoices, handleDirectPDF, invoiceTypeId, InvtypeidFilter]);

  // إعادة تعيين الفلاتر
  const resetFilters = useCallback(() => {
    range.reset(); // اليوم
    setStatusFilter("");
    setinvtypidFilter("");
    setBranchFilter("");
    setSearchTerm("");
  }, []);

  // طباعة التقرير
  const handlePrint = useCallback(() => {
    window.print();
  }, []);

  const STATUS_OPTIONS = [
    { value: 0, label: "مسودة" },
    { value: 1, label: "معتمدة" },
    { value: 2, label: "مدفوعة" },
    { value: 3, label: "ملغاة" },
  ];

  const branchName = branches.find(b => String(b.id) === String(branchFilter));
  const activeFilterCount =
    (statusFilter !== "" ? 1 : 0) + (InvtypeidFilter !== "" ? 1 : 0) +
    (branchFilter !== "" ? 1 : 0) + (searchTerm ? 1 : 0);

  return (
    <>
      <ReportPage
        title="تقرير الفواتير"
        subtitle="متابعة الفواتير وإجمالياتها حسب الفترة والفرع والحالة"
        icon={<ReceiptLongIcon />}
        period={range}
        badges={branchName ? [{ label: branchName.nameAr || branchName.name, icon: <StorefrontOutlinedIcon /> }] : []}
        actions={[
          { label: "تحديث", icon: <RefreshIcon />, onClick: fetchAllInvoices, disabled: loading },
          { label: "طباعة التقرير", icon: <PrintIcon />, onClick: handlePrint, primary: true },
        ]}
        stats={[
          { label: "عدد الفواتير", value: formatNumber(filteredInvoices.length), icon: <EqualizerIcon />, color: "primary" },
          { label: "إجمالي المبالغ", value: formatCurrency(totals.totalBeforeTax), icon: <AttachMoneyIcon />, color: "info" },
          { label: "إجمالي الخصومات", value: formatCurrency(totals.discountTotal), icon: <LocalOfferIcon />, color: "warning" },
          { label: "إجمالي الضرائب", value: formatCurrency(totals.taxTotal), icon: <ReceiptLongIcon />, color: "error" },
          { label: "الصافي النهائي", value: formatCurrency(totals.totalAfterTax), icon: <AccountBalanceWalletIcon />, color: "success", highlight: true },
        ]}
        filters={
          <ReportFilters
            range={range}
            search={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="رقم الفاتورة، العميل، النوع..."
            onApply={fetchAllInvoices}
            onReset={resetFilters}
            loading={loading}
            activeCount={activeFilterCount}
          >
            <ReportSelect
              label="الفرع"
              value={branchFilter}
              onChange={setBranchFilter}
              options={branches.map(b => ({ value: String(b.id), label: b.nameAr || b.name }))}
            />
            <ReportSelect
              label="نوع الفاتورة"
              value={InvtypeidFilter}
              onChange={setinvtypidFilter}
              options={invoiceTypes.map(t => ({ value: String(t.invoiceTypeId), label: t.name }))}
            />
            <ReportSelect
              label="الحالة"
              value={statusFilter}
              onChange={setStatusFilter}
              options={STATUS_OPTIONS}
            />
          </ReportFilters>
        }
      >
        <ReportTableCard title="الفواتير" count={formatNumber(filteredInvoices.length)}>
                <DataGrid
                  rows={mappedRows}
                  columns={columns}
                  getRowId={(row) => row.invGlobalID}
                  loading={loading}
                  components={{ Toolbar: GridToolbar }}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 25 },
                    },
                  }}
                  pageSizeOptions={[10, 25, 50, 100]}
                  disableSelectionOnClick
                  localeText={{
                    // ترجمة النصوص للعربية
                    noRowsLabel: 'لا توجد بيانات',
                    noResultsOverlayLabel: 'لم يتم العثور على نتائج',
                    errorOverlayDefaultLabel: 'حدث خطأ',
                    toolbarColumns: 'الأعمدة',
                    toolbarFilters: 'الفلاتر',
                    toolbarDensity: 'الكثافة',
                    toolbarExport: 'تصدير',
                    columnsPanelTextFieldLabel: 'البحث في الأعمدة',
                    columnsPanelShowAllButton: 'إظهار الكل',
                    columnsPanelHideAllButton: 'إخفاء الكل',
                    filterPanelAddFilter: 'إضافة فلتر',
                    filterPanelDeleteIconLabel: 'حذف',
                    filterPanelOperators: 'العمليات',
                    filterPanelOperatorAnd: 'و',
                    filterPanelOperatorOr: 'أو',
                    filterPanelColumns: 'الأعمدة',
                    filterPanelInputLabel: 'القيمة',
                    filterPanelInputPlaceholder: 'قيمة الفلتر',
                    filterOperatorContains: 'يحتوي على',
                    filterOperatorEquals: 'يساوي',
                    filterOperatorStartsWith: 'يبدأ بـ',
                    filterOperatorEndsWith: 'ينتهي بـ',
                    filterOperatorIsEmpty: 'فارغ',
                    filterOperatorIsNotEmpty: 'غير فارغ',
                    columnMenuLabel: 'القائمة',
                    columnMenuShowColumns: 'إظهار الأعمدة',
                    columnMenuFilter: 'فلتر',
                    columnMenuHideColumn: 'إخفاء',
                    columnMenuUnsort: 'إلغاء الترتيب',
                    columnMenuSortAsc: 'ترتيب تصاعدي',
                    columnMenuSortDesc: 'ترتيب تنازلي',
                    footerRowSelected: (count) => `${formatNumber(count)} صف محدد`,
                    footerTotalRows: 'إجمالي الصفوف:',
                    footerTotalVisibleRows: (visibleCount, totalCount) =>
                      `${formatNumber(visibleCount)} من ${formatNumber(totalCount)}`,
                  }}
                  sx={reportGridSx}
                />
        </ReportTableCard>
      </ReportPage>

      {/* محتوى الطباعة فقط */}
        <Box
          sx={{
            display: 'none',
            '@media print': { display: 'block' },
            p: 3,
            color: '#000',
            backgroundColor: '#fff'
          }}
          dir="rtl"
        >
          {/* رأس الشركة */}
          {companyInfo && <CompanyHeader company={companyInfo} />}
          
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>📑 تقرير الفواتير</Typography>
            <Typography variant="body2">
              الفترة: من {dateFrom} إلى {dateTo}
            </Typography>
            <Typography variant="body2">
              الحالة: {statusFilter === '' ? 'الكل' : (statusFilter == 0 ? 'مسودة' : statusFilter == 1 ? 'معتمدة' : statusFilter == 2 ? 'مدفوعة' : statusFilter == 3 ? 'ملغاة' : 'غير معروف')}
            </Typography>
            <Typography variant="body2">
              نوع الفاتورة: {(InvtypeidFilter === '' && (invoiceTypeId === null || invoiceTypeId === undefined)) ? 'الكل' : (invoiceTypes.find(t => Number(t.id) === Number(InvtypeidFilter !== '' ? InvtypeidFilter : invoiceTypeId))?.name ?? '—')}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              تم الطباعة في: {dayjs().format('YYYY-MM-DD HH:mm')}
            </Typography>
          </Box>

          {/* ملخص المجاميع */}
          <Box sx={{ mb: 2 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <tbody>
                <tr>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>إجمالي المبالغ</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>{formatCurrency(totals.totalBeforeTax)}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>إجمالي الخصومات</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>{formatCurrency(totals.discountTotal)}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc' }}>إجمالي الضرائب</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center' }}>{formatCurrency(totals.taxTotal)}</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', fontWeight: 'bold' }}>الصافي النهائي</td>
                  <td style={{ padding: '6px', border: '1px solid #ccc', textAlign: 'center', fontWeight: 'bold' }}>{formatCurrency(totals.totalAfterTax)}</td>
                </tr>
              </tbody>
            </table>
          </Box>

          {/* جدول الطباعة */}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead>
              <tr style={{ background: '#f5f5f5' }}>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>م</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>التاريخ</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>رقم الفاتورة</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>نوع الفاتورة</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>العميل/المورد</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الإجمالي</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الخصم</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>بعد الخصم</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الضريبة</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الصافي</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>طريقة الدفع</th>
                <th style={{ border: '1px solid #ccc', padding: '6px' }}>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {mappedRows.map((row, idx) => (
                <tr key={row.invGlobalID}>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{idx + 1}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.issueDate}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{row.invoiceNumber}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.invoiceTypeName}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.partyName}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.totalBeforeTax)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.discountTotal)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.totalAfterDiscount)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed' }}>{formatCurrency(row.taxTotal)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center', direction: 'ltr', unicodeBidi: 'embed', fontWeight: 'bold' }}>{formatCurrency(row.totalAfterTax)}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>{row.paymentMethodName}</td>
                  <td style={{ border: '1px solid #eee', padding: '6px', textAlign: 'center' }}>
                    {row.invoiceStatus === 0 ? 'مسودة' : row.invoiceStatus === 1 ? 'معتمدة' : row.invoiceStatus === 2 ? 'مدفوعة' : row.invoiceStatus === 3 ? 'ملغاة' : 'غير معروف'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
    </>
  );
}
